import { useRouter } from 'next/router';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';

/**
 * Configuration for a single filter parameter
 */
interface FilterConfig {
  /** Query parameter name in the URL */
  paramName: string;
  /** Default value when parameter is not present */
  defaultValue?: string | string[];
  /** Whether this parameter accepts multiple values (array) */
  isArray?: boolean;
}

/**
 * Map of filter keys to their configurations
 */
type FiltersConfig = Record<string, FilterConfig>;

/**
 * Parsed filter values from URL
 */
type FilterValues<T extends FiltersConfig> = {
  [K in keyof T]: T[K]['isArray'] extends true ? string[] : string;
};

/**
 * Update handlers for each filter
 */
type FilterHandlers<T extends FiltersConfig> = {
  [K in keyof T]: (value: T[K]['isArray'] extends true ? string[] : string) => void;
};

interface UseUrlSyncOptions<T extends FiltersConfig> {
  /** Configuration for each filter parameter */
  filters: T;
}

interface UseUrlSyncReturn<T extends FiltersConfig> {
  /** Current filter values parsed from URL */
  values: FilterValues<T>;
  /** Update handlers for each filter */
  handlers: FilterHandlers<T>;
  /** Whether the router is ready */
  isReady: boolean;
}

/**
 * Hook for synchronizing filter values with URL query parameters
 *
 * @example
 * ```tsx
 * const { values, handlers, isReady } = useUrlSync({
 *   filters: {
 *     search: { paramName: 'search', defaultValue: '' },
 *     status: { paramName: 'status', defaultValue: 'active' },
 *     regions: { paramName: 'regions', defaultValue: [], isArray: true },
 *   }
 * });
 *
 * // Use values
 * <SearchInput value={values.search} onChange={handlers.search} />
 * <RegionSelector selectedRegions={values.regions} onRegionsChange={handlers.regions} />
 * ```
 */
export function useUrlSync<T extends FiltersConfig>({
  filters,
}: UseUrlSyncOptions<T>): UseUrlSyncReturn<T> {
  const router = useRouter();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (router.isReady) {
      setIsReady(true);
    }
  }, [router.isReady]);

  // Parse current values from URL.
  //
  // IMPORTANT: array params must keep a stable reference between renders when
  // the underlying value hasn't changed. `router.query` is a new object on every
  // (even shallow) navigation, so keying the memo off it directly would produce a
  // fresh `[value]` array each render. Downstream effects that depend on that
  // array reference (e.g. ContentTable resetting its cursor) would then re-fire
  // and trigger another shallow push, causing an infinite render/navigation loop
  // (100% CPU). We key the memo off a serialized snapshot of just the params we
  // care about so the returned object is referentially stable by content.
  const valuesSignature = useMemo(() => {
    const snapshot: Record<string, unknown> = {};
    for (const key in filters) {
      snapshot[key] = router.query[filters[key].paramName];
    }
    return JSON.stringify(snapshot);
  }, [router.query, JSON.stringify(filters)]);

  const values = useMemo(() => {
    const parsedValues = {} as FilterValues<T>;

    for (const key in filters) {
      const config = filters[key];
      const queryValue = router.query[config.paramName];

      if (config.isArray) {
        // Handle array parameters
        if (queryValue) {
          parsedValues[key] = (Array.isArray(queryValue) ? queryValue : [queryValue]) as any;
        } else {
          parsedValues[key] = (config.defaultValue || []) as any;
        }
      } else {
        // Handle single value parameters
        parsedValues[key] = ((queryValue as string) || config.defaultValue || '') as any;
      }
    }
    return parsedValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesSignature, JSON.stringify(filters)]);

  // Batch updates that happen in the same tick to avoid race conditions
  // (e.g. when ObjectBrowser's applyMobileFilters calls onSearchChange + onFiltersChange together)
  const pendingUpdatesRef = useRef<Record<string, string | string[] | undefined>>({});
  const flushScheduledRef = useRef(false);

  const flushUpdates = useCallback(() => {
    const updates = pendingUpdatesRef.current;
    pendingUpdatesRef.current = {};
    flushScheduledRef.current = false;

    const newQuery = { ...router.query, ...updates };

    // Remove empty values
    Object.keys(newQuery).forEach((key) => {
      if (!newQuery[key] || (Array.isArray(newQuery[key]) && newQuery[key].length === 0)) {
        delete newQuery[key];
      }
    });

    // Store current scroll position
    const scrollY = window.scrollY;

    router
      .push(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true, scroll: false }
      )
      .then(() => {
        // Restore scroll position after navigation
        window.scrollTo(0, scrollY);
      });
  }, [router]);

  // Update query params helper — batches multiple calls in the same tick
  const updateQueryParams = useCallback(
    (updates: Record<string, string | string[] | undefined>) => {
      Object.assign(pendingUpdatesRef.current, updates);
      if (!flushScheduledRef.current) {
        flushScheduledRef.current = true;
        // Use queueMicrotask to flush after all synchronous handlers have run
        queueMicrotask(flushUpdates);
      }
    },
    [flushUpdates]
  );

  // Generate handlers for each filter
  const handlers = useMemo(() => {
    const newHandlers = {} as FilterHandlers<T>;

    for (const key in filters) {
      const config = filters[key];

      newHandlers[key] = ((value: string | string[]) => {
        if (config.isArray) {
          // Handle array parameters
          const arrayValue = value as string[];
          updateQueryParams({
            [config.paramName]: arrayValue.length > 0 ? arrayValue : undefined,
          });
        } else {
          // Handle single value parameters
          const stringValue = value as string;
          // Only set to undefined if it matches the default value
          const shouldClear = stringValue === config.defaultValue || !stringValue;
          updateQueryParams({
            [config.paramName]: shouldClear ? undefined : stringValue,
          });
        }
      }) as any;
    }
    return newHandlers;
  }, [updateQueryParams, JSON.stringify(filters)]);

  return {
    values,
    handlers,
    isReady,
  };
}
